import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from 'react';
import { t } from '@/lib/i18n';
import { agentSessionsService, type AgentSessionDTO } from '@/services/roros/agent-sessions.service';
import type { ChatMessage } from '../components/AgentChatPageParts';
import { mapAgentSessionMessages } from '../agentSessionMessageMapping';
import { getAgentSessionErrorMessage } from '../agentSessionErrorMessage';

type SessionSync = (session: AgentSessionDTO) => void;

type UseAgentSessionActionsArgs = {
  selectedActorId: string;
  activeSession: AgentSessionDTO | null;
  setActiveSession: Dispatch<SetStateAction<AgentSessionDTO | null>>;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  setStartError: Dispatch<SetStateAction<string | null>>;
  setConfirmationRefreshToken: Dispatch<SetStateAction<number>>;
  prependSession: (session: AgentSessionDTO) => void;
  replaceSession: (session: AgentSessionDTO) => void;
  syncUsageFromSession: SessionSync;
  syncCountersFromSession: SessionSync;
  syncTierFromSession: SessionSync;
  abortRef: MutableRefObject<boolean>;
};

export function useAgentSessionActions({
  selectedActorId,
  activeSession,
  setActiveSession,
  setMessages,
  setStartError,
  setConfirmationRefreshToken,
  prependSession,
  replaceSession,
  syncUsageFromSession,
  syncCountersFromSession,
  syncTierFromSession,
  abortRef,
}: UseAgentSessionActionsArgs) {
  const startSession = useCallback(async () => {
    if (!selectedActorId || selectedActorId === '') return;
    setStartError(null);
    try {
      const newSession = await agentSessionsService.createSession({ actorId: selectedActorId });
      setActiveSession(newSession);
      setMessages([]);
      syncUsageFromSession(newSession);
      prependSession(newSession);
    } catch (err: unknown) {
      setStartError(getAgentSessionErrorMessage(err) ?? t('Failed to start session'));
    }
  }, [selectedActorId, setActiveSession, setMessages, setStartError, syncUsageFromSession, prependSession]);

  const openSession = useCallback(async (session: AgentSessionDTO) => {
    setActiveSession(session);
    setMessages([]);
    syncCountersFromSession(session);

    // AC-AGS-4: re-hydrate transcript from REST on open
    try {
      const detail = await agentSessionsService.getSession(session.id);
      setActiveSession(detail.session);
      syncTierFromSession(detail.session);
      setMessages(mapAgentSessionMessages(detail.messages));
    } catch {
      // non-fatal
    }
  }, [setActiveSession, setMessages, syncCountersFromSession, syncTierFromSession]);

  // AC-M6-6: re-fetch messages for the active session (called after confirmation resolved)
  const refetchMessages = useCallback(async () => {
    if (!activeSession) return;
    try {
      const detail = await agentSessionsService.getSession(activeSession.id);
      setActiveSession(detail.session);
      setMessages(mapAgentSessionMessages(detail.messages));
      setConfirmationRefreshToken((n) => n + 1);
    } catch {
      // non-fatal
    }
  }, [activeSession, setActiveSession, setMessages, setConfirmationRefreshToken]);

  const cancelSession = useCallback(async () => {
    if (!activeSession) return;
    try {
      abortRef.current = true;
      const updated = await agentSessionsService.cancelSession(activeSession.id);
      setActiveSession(updated);
      replaceSession(updated);
    } catch {
      // non-fatal
    }
  }, [activeSession, setActiveSession, replaceSession, abortRef]);

  return {
    startSession,
    openSession,
    refetchMessages,
    cancelSession,
  };
}
